(() => {
  "use strict";

  const STORAGE_KEY = "wsnzAudioPreferences";
  const DEFAULT_VOLUME = 0.7;
  const DEFAULT_EFFECT_VOLUME = 0.58;

  const listeners = new Set();
  const effectPools = new Map();
  const effectLastPlayed = new Map();
  const uiMounts = new WeakMap();

  const state = {
    track: null,
    playing: false,
    muted: false,
    volume: DEFAULT_VOLUME,
    currentTime: 0,
    duration: 0,
    status: "Ready",
    error: ""
  };

  let audio = null;
  let fadeFrame = 0;
  let resumeAfterVisibility = false;

  function clamp(value, min = 0, max = 1) {
    return Math.min(max, Math.max(min, Number(value) || 0));
  }

  function loadPreferences() {
    try {
      const saved = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || "{}"
      );

      state.volume = clamp(
        saved.volume ?? DEFAULT_VOLUME
      );

      state.muted = Boolean(saved.muted);
    } catch {
      state.volume = DEFAULT_VOLUME;
      state.muted = false;
    }
  }

  function savePreferences() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          volume: state.volume,
          muted: state.muted
        })
      );
    } catch {
      // Storage may be unavailable in preview or privacy modes.
    }
  }

  function emit() {
    const snapshot = getState();

    listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch (error) {
        console.warn("[WSNZ Audio]", error);
      }
    });
  }

  function getState() {
    return {
      ...state,
      track: state.track
        ? { ...state.track }
        : null
    };
  }

  function ensureAudio() {
    if (audio) return audio;

    audio = new Audio();
    audio.preload = "metadata";
    audio.volume = state.muted
      ? 0
      : clamp(state.volume);

    audio.addEventListener("loadedmetadata", () => {
      state.duration = Number.isFinite(audio.duration)
        ? audio.duration
        : 0;

      state.status = "Ready";
      emit();
    });

    audio.addEventListener("timeupdate", () => {
      state.currentTime = audio.currentTime || 0;

      state.duration = Number.isFinite(audio.duration)
        ? audio.duration
        : state.duration;

      emit();
    });

    audio.addEventListener("play", () => {
      state.playing = true;
      state.status = "Playing";
      state.error = "";
      emit();
    });

    audio.addEventListener("pause", () => {
      state.playing = false;

      if (
        !state.error &&
        state.status !== "Stopped"
      ) {
        state.status = "Paused";
      }

      emit();
    });

    audio.addEventListener("ended", () => {
      state.playing = false;
      state.currentTime = 0;
      state.status = "Finished";
      emit();
    });

    audio.addEventListener("error", () => {
      state.playing = false;
      state.error =
        "The music asset could not be loaded.";
      state.status = "Unavailable";
      emit();
    });

    return audio;
  }

  function cancelFade() {
    if (!fadeFrame) return;

    cancelAnimationFrame(fadeFrame);
    fadeFrame = 0;
  }

  function fadeTo(target, duration = 500) {
    const media = ensureAudio();

    cancelFade();

    target = clamp(target);
    duration = Math.max(
      0,
      Number(duration) || 0
    );

    if (!duration) {
      media.volume = target;
      return Promise.resolve();
    }

    const start = clamp(media.volume);
    const began = performance.now();

    return new Promise((resolve) => {
      const step = (now) => {
        const progress = Math.min(
          1,
          Math.max(
            0,
            (now - began) / duration
          )
        );

        const nextVolume =
          start +
          (target - start) * progress;

        media.volume = clamp(nextVolume);

        if (progress < 1) {
          fadeFrame =
            requestAnimationFrame(step);
          return;
        }

        media.volume = target;
        fadeFrame = 0;
        resolve();
      };

      fadeFrame = requestAnimationFrame(step);
    });
  }

  async function playMusic(
    track,
    {
      fadeMs = 700,
      restart = false
    } = {}
  ) {
    if (!track?.src) {
      throw new Error(
        "A music source is required."
      );
    }

    const media = ensureAudio();

    const changed =
      state.track?.id !== track.id ||
      media.src !==
        new URL(
          track.src,
          document.baseURI
        ).href;

    if (changed) {
      if (!media.paused) {
        await fadeTo(
          0,
          Math.min(fadeMs, 400)
        );
      }

      media.pause();
      media.src = track.src;
      media.currentTime = 0;
      media.loop = Boolean(track.loop);

      state.currentTime = 0;
      state.duration = 0;
      state.track = {
        id: String(
          track.id || track.src
        ),
        title: String(
          track.title || "WSNZ Music"
        ),
        src: track.src,
        loop: Boolean(track.loop)
      };
    } else {
      media.loop = Boolean(track.loop);

      if (restart) {
        media.currentTime = 0;
      }
    }

    state.error = "";
    state.status = "Loading";
    emit();

    media.volume = 0;

    try {
      await media.play();

      await fadeTo(
        state.muted
          ? 0
          : state.volume,
        fadeMs
      );

      return true;
    } catch {
      state.playing = false;
      state.status = "Playback blocked";
      state.error =
        "Select Play to begin the anthem.";
      emit();

      return false;
    }
  }

  function pause() {
    if (!audio || audio.paused) return;

    cancelFade();
    audio.pause();
    state.status = "Paused";
    emit();
  }

  async function stopMusic(
    { fadeMs = 500 } = {}
  ) {
    if (!audio) return;

    await fadeTo(0, fadeMs);

    audio.pause();
    audio.currentTime = 0;

    state.currentTime = 0;
    state.status = "Stopped";
    emit();
  }

  function seek(seconds) {
    if (
      !audio ||
      !Number.isFinite(audio.duration)
    ) {
      return;
    }

    audio.currentTime = Math.min(
      audio.duration,
      Math.max(
        0,
        Number(seconds) || 0
      )
    );
  }

  function setVolume(value) {
    state.volume = clamp(value);
    savePreferences();

    if (audio && !state.muted) {
      audio.volume = clamp(state.volume);
    }

    effectPools.forEach((record) => {
      record.pool.forEach((media) => {
        media.volume = state.muted
          ? 0
          : clamp(
              DEFAULT_EFFECT_VOLUME *
              state.volume
            );
      });
    });

    emit();
  }

  function setMuted(value) {
    state.muted = Boolean(value);
    savePreferences();

    if (audio) {
      audio.volume = state.muted
        ? 0
        : clamp(state.volume);
    }

    effectPools.forEach((record) => {
      record.pool.forEach((media) => {
        media.volume = state.muted
          ? 0
          : clamp(
              DEFAULT_EFFECT_VOLUME *
              state.volume
            );
      });
    });

    emit();
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      return () => {};
    }

    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  }

  function mountAmbientMusic(
    track,
    { fadeMs = 900 } = {}
  ) {
    if (!track?.src) return () => {};

    let disposed = false;
    let started = false;

    const removeUnlockListeners = () => {
      document.removeEventListener(
        "pointerdown",
        begin,
        true
      );

      document.removeEventListener(
        "keydown",
        begin,
        true
      );

      document.removeEventListener(
        "touchstart",
        begin,
        true
      );
    };

    const begin = () => {
      if (disposed || started) return;

      started = true;
      removeUnlockListeners();

      playMusic(
        {
          ...track,
          loop: true
        },
        {
          fadeMs
        }
      ).catch((error) => {
        started = false;
        console.warn(
          "[WSNZ Audio]",
          error
        );
      });
    };

    document.addEventListener(
      "pointerdown",
      begin,
      {
        capture: true,
        once: true
      }
    );

    document.addEventListener(
      "keydown",
      begin,
      {
        capture: true,
        once: true
      }
    );

    document.addEventListener(
      "touchstart",
      begin,
      {
        capture: true,
        once: true,
        passive: true
      }
    );

    return () => {
      disposed = true;
      removeUnlockListeners();
    };
  }

  function getEffectPool(effect) {
    const id = String(
      effect?.id ||
      effect?.src ||
      "effect"
    );

    if (effectPools.has(id)) {
      return effectPools.get(id);
    }

    const size = Math.max(
      1,
      Math.min(
        4,
        Number(effect?.poolSize) || 2
      )
    );

    const pool = Array.from(
      { length: size },
      () => {
        const media =
          new Audio(effect.src);

        media.preload = "auto";

        media.volume = state.muted
          ? 0
          : clamp(
              (
                effect.volume ??
                DEFAULT_EFFECT_VOLUME
              ) * state.volume
            );

        return media;
      }
    );

    const record = {
      pool,
      index: 0
    };

    effectPools.set(id, record);

    return record;
  }

  function playEffect(
    effect,
    { restart = true } = {}
  ) {
    if (!effect?.src || state.muted) {
      return false;
    }

    const id = String(
      effect.id || effect.src
    );

    const now = performance.now();

    const cooldown = Math.max(
      0,
      Number(effect.cooldown) || 0
    );

    if (
      cooldown &&
      now -
        (effectLastPlayed.get(id) || 0) <
        cooldown
    ) {
      return false;
    }

    effectLastPlayed.set(id, now);

    const record =
      getEffectPool(effect);

    const media =
      record.pool[
        record.index++ %
        record.pool.length
      ];

    media.volume = clamp(
      (
        effect.volume ??
        DEFAULT_EFFECT_VOLUME
      ) * state.volume
    );

    if (restart) {
      try {
        media.currentTime = 0;
      } catch {
        // Some browsers may reject seeking
        // before metadata is available.
      }
    }

    media.play().catch(() => {});

    return true;
  }

  function mountUISounds(
    root,
    config = {}
  ) {
    if (!root || uiMounts.has(root)) {
      return () => {};
    }

    const effects =
      config.effects || {};

    let lastHover = null;

    const closest = (
      target,
      selector
    ) => {
      return (
        selector &&
        target instanceof Element
      )
        ? target.closest(selector)
        : null;
    };

    const onPointerOver = (event) => {
      const hit = closest(
        event.target,
        config.hoverSelector
      );

      if (
        !hit ||
        !root.contains(hit) ||
        hit === lastHover
      ) {
        return;
      }

      lastHover = hit;

      playEffect({
        ...effects.hover,
        cooldown: 70
      });
    };

    const onPointerOut = (event) => {
      const hit = closest(
        event.target,
        config.hoverSelector
      );

      if (
        hit &&
        (
          !event.relatedTarget ||
          !hit.contains(
            event.relatedTarget
          )
        )
      ) {
        lastHover = null;
      }
    };

    const onClick = (event) => {
      const rules =
        config.clickRules || [];

      for (const rule of rules) {
        const hit = closest(
          event.target,
          rule.selector
        );

        if (
          hit &&
          root.contains(hit)
        ) {
          playEffect(
            effects[rule.effect]
          );
          break;
        }
      }
    };

    root.addEventListener(
      "pointerover",
      onPointerOver
    );

    root.addEventListener(
      "pointerout",
      onPointerOut
    );

    root.addEventListener(
      "click",
      onClick,
      true
    );

    const onUnlock = () => {
      playEffect(effects.unlock);
    };

    const onAchievement = () => {
      playEffect(effects.achievement);
    };

    root.addEventListener(
      "wsnz:unlock",
      onUnlock
    );

    root.addEventListener(
      "wsnz:achievement",
      onAchievement
    );

    const dispose = () => {
      root.removeEventListener(
        "pointerover",
        onPointerOver
      );

      root.removeEventListener(
        "pointerout",
        onPointerOut
      );

      root.removeEventListener(
        "click",
        onClick,
        true
      );

      root.removeEventListener(
        "wsnz:unlock",
        onUnlock
      );

      root.removeEventListener(
        "wsnz:achievement",
        onAchievement
      );

      uiMounts.delete(root);
    };

    uiMounts.set(root, dispose);

    return dispose;
  }

  document.addEventListener(
    "visibilitychange",
    () => {
      if (document.hidden) {
        resumeAfterVisibility =
          Boolean(
            audio &&
            !audio.paused
          );

        if (resumeAfterVisibility) {
          pause();
        }

        return;
      }

      if (
        resumeAfterVisibility &&
        state.track
      ) {
        resumeAfterVisibility = false;

        playMusic(
          state.track,
          { fadeMs: 500 }
        );
      }
    }
  );

  window.addEventListener(
    "pagehide",
    () => {
      if (
        audio &&
        !audio.paused
      ) {
        audio.pause();
      }
    }
  );

  loadPreferences();

  window.WSNZ =
    window.WSNZ || {};

  window.WSNZ.audio = {
    playMusic,
    mountAmbientMusic,
    playEffect,
    mountUISounds,
    pause,
    stopMusic,
    seek,
    setVolume,
    setMuted,
    subscribe,
    getState
  };

  emit();
})();
